import { useForm } from '@tanstack/react-form';
import { createContext, useContext } from 'react';
import {
  type ShippingMethodBuilderState,
  type ShippingMethodBuilderValidated,
  shippingMethodBuilderSchema,
} from './schemas';

interface ShippingFormProps {
  initialState?: ShippingMethodBuilderState;
  onSubmit?: (payload: ShippingMethodBuilderValidated) => Promise<void> | void;
  onError?: () => Promise<void> | void;
}

export function useShippingFormLogic({ initialState, onSubmit, onError }: ShippingFormProps) {
  return useForm({
    defaultValues:
      initialState ??
      ({
        name: '',
        sku: '',
        priority: '0',
        countries: [],
        customerGroups: [],
        regions: {},
        bands: [],
      } satisfies ShippingMethodBuilderState),
    validators: {
      onSubmit: shippingMethodBuilderSchema,
    },
    onSubmit: async ({ value }) => {
      const payload = shippingMethodBuilderSchema.parse(value);
      await onSubmit?.(payload);
    },
    onSubmitInvalid: onError,
  });
}

type ShippingFormApi = ReturnType<typeof useShippingFormLogic>;

export const ShippingMethodFormContext = createContext<ShippingFormApi | null>(null);

export function useShippingMethodForm() {
  const context = useContext(ShippingMethodFormContext);

  if (!context) {
    throw new Error(
      'useShippingMethodForm must be used within a ShippingMethodFormContext.Provider',
    );
  }

  return context;
}
